/**
 * API → template shape mappers for the 5 dashboard APIs documented in
 * Dashboard-APIs-Quick-Reference.md. Keeps the template shapes stable so
 * the component HTML doesn't need to change when the API contract evolves.
 */

import { AppSettings } from '../../../services/AppSettings';

// ── Shared formatting helpers ────────────────────────────────────────



/** Tone of the AI-score / match badge. `''` means "no score" — the template
 *  renders `-` and no `.mb-*` rule applies, so the badge stays uncoloured. */
export type MatchTone = 'green' | 'amber' | 'red' | 'grey';

/**
 * Platform-wide AI-score colour tiers:
 *   >= 75  green   (#D1FAE5 / --gs-green)
 *   40-74  amber   (#FEF3C7 / --gs-amber)
 *   <  40  red     (#FEE2E2 / --gs-red)
 *   none   ''      (no colour)
 *
 * Identical to `jobMatchTier()` in job-card.component.ts and `matchTier()` in
 * candidate-card-table-view.component.ts, which both mirror
 * `.jc-v3-c-match-badge{,.amber,.red}` in job-center-v3.scss. The dashboards
 * previously used a 2-tier rule (`>= 80 ? green : amber`) with no red band, so
 * the same score could read green on a job card and amber on the dashboard, and
 * weak scores were never flagged. Every dashboard match badge routes through
 * here so the tiers can only ever change in one place.
 */
export function matchTone(match: number | null | undefined): MatchTone {
  if (match == null) return 'grey';
  if (match >= 75) return 'green';
  if (match >= 40) return 'amber';
  return 'red';
}

/** Pay-period suffix per the `payType` the offer APIs return. The canonical
 *  backend vocabulary is `Hourly | Daily | Weekly | Monthly | Yearly` (set from
 *  `JobApplyLog.payType`), but older records / other endpoints have used the
 *  bare-noun and annual spellings, so accept those too rather than silently
 *  dropping the period. */
const PAY_PERIOD_SUFFIX: Record<string, string> = {
  HOURLY: 'hr', DAILY: 'day', WEEKLY: 'week', MONTHLY: 'mo', YEARLY: 'yr',
  HOUR: 'hr', DAY: 'day', WEEK: 'week', MONTH: 'mo',
  YEAR: 'yr', ANNUAL: 'yr', ANNUALLY: 'yr',
};

/** "$130,000/yr", "$95/hr", "$6,000/mo" — the Offer Pipeline Tracker amount.
 *  Every rate gets the `$` and thousands separators regardless of pay type; an
 *  unknown or missing `payType` only costs the `/period` suffix. Earlier this
 *  handled YEARLY/HOURLY alone and fell through to the raw value, so a monthly
 *  offer rendered as a bare `6000` on both dashboards. */

export function formatRate(targetRate: any, payType: string | null | undefined): string {
  if (targetRate == null || targetRate === '') return '';
  const n = Number(targetRate);
  // Non-numeric values (e.g. a "6000-8000" range string) are kept verbatim
  // instead of becoming NaN — they still get the symbol and the period.
  const amount = Number.isFinite(n)
    ? n.toLocaleString('en-US', { maximumFractionDigits: 2 })
    : String(targetRate).trim();
  if (!amount) return '';
  const money = /^[$€£₹]/.test(amount) ? amount : `$${amount}`;
  const suffix = PAY_PERIOD_SUFFIX[(payType || '').trim().toUpperCase()];
  return suffix ? `${money}/${suffix}` : money;
}

function toTitleCase(value: string): string {
  if (!value) return '';
  return value
    .toLowerCase()
    .split(/([-_\s]+)/)
    .map(part => /^[-_\s]+$/.test(part) ? part : part.charAt(0).toUpperCase() + part.slice(1))
    .join('');
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function formatDate(input: string | number | Date | null | undefined): string {
  if (input == null || input === '') return '';
  const d = input instanceof Date ? input : new Date(input);
  if (isNaN(d.getTime())) return '';
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

/** "Tuesday, April 7, 2026" — used by the dashboard welcome banners. */
// export function formatLongDate(d: Date = new Date()): string {
//   return `${WEEKDAYS_LONG[d.getDay()]}, ${MONTHS_LONG[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
// }

// Resolve a usable IANA time zone from the param → localStorage → system
// default. `??` alone isn't enough: localStorage.getItem returns "" (not null)
// for an empty key, and that empty/invalid string makes Intl.DateTimeFormat
// throw `RangeError: Invalid time zone specified`. Empty/whitespace/invalid
// values fall back to undefined, which uses the runtime's local time zone.
function resolveTimeZone(timeZone?: string): string | undefined {
  const raw = (timeZone ?? localStorage.getItem('timezonedate') ?? '').trim();
  if (!raw) return undefined;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: raw });
    return raw;
  } catch {
    return undefined;
  }
}

export function formatLongDate(d: Date = new Date(), timeZone?: string): string {
  const tz = resolveTimeZone(timeZone);
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: tz,
  }).format(d);
}

export function getgreeting(d: Date = new Date(), timeZone?: string): string {const tz = resolveTimeZone(timeZone);

  const hour = parseInt(
    new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      hour12: false,
      timeZone: tz,
    }).format(d),
    10
  ) % 24;
 
  if (hour >= 5 && hour < 12) return "Good Morning";
  if (hour >= 12 && hour < 17) return "Good Afternoon";
  if (hour >= 17 && hour < 21) return "Good Evening";
  return "Good Night";
}

// function formatShortDate(input: number | null | undefined): string {
//   if (input == null) return '';
//   const d = new Date(input);
//   if (isNaN(d.getTime())) return '';
//   return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
// }

function formatShortDate(input: number | null | undefined): string {
  if (input == null) return '';
  const d = new Date(input);
  if (isNaN(d.getTime())) return '';
  // Render "Aug 4" in the user's saved timezone (localStorage `timezonedate`),
  // matching the other date helpers here. Using getUTCMonth/getUTCDate printed
  // the UTC calendar date, so near a midnight boundary the day was off by one.
  const tz = resolveTimeZone();
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: tz,
  }).format(d);
}

// ── Weekly Applicants (Recruiter Hiring Pipeline card) ──────────────
//
// Maps the `weeklyApplicants` block returned by jobs/getBusinessJobPipelineStats
// (added 2026-04-25) into the [{day,val,active?}] shape consumed by the
// "Applicants This Week" mini-chart. `active` flags today's bar so the
// template highlights it.

const WEEKDAY_KEYS: { key: string; day: string; idx: number }[] = [
  { key: 'Monday', day: 'Mon', idx: 1 },
  { key: 'Tuesday', day: 'Tue', idx: 2 },
  { key: 'Wednesday', day: 'Wed', idx: 3 },
  { key: 'Thursday', day: 'Thu', idx: 4 },
  { key: 'Friday', day: 'Fri', idx: 5 },
  { key: 'Saturday', day: 'Sat', idx: 6 },
  { key: 'Sunday', day: 'Sun', idx: 0 },
];

// "Today" for the Applicants-This-Week highlight must follow the user's saved
// timezone (set on login/profile update under localStorage `timezonedate`),
// not the browser's local zone — a recruiter in IST viewing the app from a
// laptop still on a US zone should see their IST weekday highlighted.
function getTodayIdxInUserTimezone(): number {
  const SHORT_TO_IDX: Record<string, number> = {
    Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6,
  };
  try {
    const stored = localStorage.getItem('timezonedate');
    const timeZone = (stored && stored !== 'null' && stored !== 'undefined' && stored.trim() !== '')
      ? stored
      : Intl.DateTimeFormat().resolvedOptions().timeZone;
    const shortDay = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short' }).format(new Date());
    const idx = SHORT_TO_IDX[shortDay];
    return idx ?? new Date().getDay();
  } catch {
    return new Date().getDay();
  }
}

export function mapWeeklyApplicants(
  weeklyApplicants: any
): { day: string; val: number; active: boolean }[] | null {
  if (!weeklyApplicants) return null;
  const todayIdx = getTodayIdxInUserTimezone();
  // Backend currently emits Title-Case keys (`Monday`, `Tuesday`, ...). An
  // earlier draft of this API used UPPERCASE keys, so check both casings to
  // stay safe across rolling deploys.
  const lookup = (key: string) =>
    weeklyApplicants[key] ?? weeklyApplicants[key.toUpperCase()] ?? 0;
  return WEEKDAY_KEYS.map(({ key, day, idx }) => ({
    day,
    val: Number(lookup(key)),
    active: idx === todayIdx,
  }));
}

// ── Weekly Activity Metrics ──────────────────────────────────────────

interface MetricItem { count: number; total: number; label: string; }

const GOAL_COLORS: Record<string, string> = {
  jobsPosted: 'var(--gs-purple-light)',
  candidatesShortlisted: 'var(--gs-amber)',
  interviewsScheduled: 'var(--gs-green)',
  offersExtended: 'var(--gs-blue)',
  submissions: 'var(--gs-blue)',
  interviewsSecured: 'var(--gs-amber)',
  followUpsSent: 'var(--gs-purple-light)',
  placements: 'var(--gs-green)',
};

// Fallback labels — the backend currently returns `label: null` for some
// metrics (e.g. `followUpsSent` on the bench dashboard), which would
// otherwise drop the tile from the Weekly Activity Metrics card.
const GOAL_DEFAULT_LABELS: Record<string, string> = {
  jobsPosted: 'Jobs Posted',
  candidatesShortlisted: 'Candidates Shortlisted',
  interviewsScheduled: 'Interviews Scheduled',
  offersExtended: 'Offers Extended',
  submissions: 'Submissions',
  interviewsSecured: 'Interviews Secured',
  followUpsSent: 'Follow-Ups Sent',
  placements: 'Placements',
};

export function mapWeeklyMetrics(metrics: any): { range: string; goals: any[] } | null {
  if (!metrics) return null;
  const goals: any[] = [];
  // Order matters — match the order shown in the mock / design.
  // `totalOnly` goals (currently: submissions) render a single count
  // instead of the `{current} / {target}` progress format — the backend
  // Submissions metric is a cumulative counter, not a goal-against-target.
  const TOTAL_ONLY_KEYS = new Set(['submissions']);
  for (const key of [
    'jobsPosted', 'candidatesShortlisted', 'interviewsScheduled', 'offersExtended',
    'submissions', 'interviewsSecured', 'followUpsSent', 'placements',
  ]) {
    const m: MetricItem | null = metrics[key];
    if (m) {
      goals.push({
        name: m.label ?? GOAL_DEFAULT_LABELS[key] ?? key,
        current: m.count ?? 0,
        target: m.total ?? 0,
        fill: GOAL_COLORS[key] || 'var(--gs-purple-light)',
        totalOnly: TOTAL_ONLY_KEYS.has(key),
      });
    }
  }
  return { range: metrics.weekRange || '', goals };
}

// ── Offer Pipeline ───────────────────────────────────────────────────

export function mapOfferStages(summary: any): any[] {
  if (!summary) return [];
  return [
    { cls: 'osc-extended', label: 'Extended', val: summary.extended ?? 0, sub: summary.extendedLabel || 'Total this month' },
    { cls: 'osc-pending', label: 'Pending', val: summary.pending ?? 0, sub: summary.pendingLabel || 'Awaiting response' },
    { cls: 'osc-accepted', label: 'Accepted', val: summary.accepted ?? 0, sub: summary.acceptedLabel || '0.0% accept rate' },
    { cls: 'osc-declined', label: 'Declined', val: summary.declined ?? 0, sub: summary.declinedLabel || '0.0% decline rate' },
  ];
}

const STATUS_ICON: Record<string, string> = {
  'OFFER-ACCEPTED': 'tablerCheck',
  'OFFERED': 'tablerHourglass',
  'OFFER-DECLINED': 'tablerX',
};
const STATUS_TONE: Record<string, string> = {
  'OFFER-ACCEPTED': 'green',
  'OFFERED': 'amber',
  'OFFER-DECLINED': 'red',
};

/** A declined offer must always read red (with the X icon and a "Declined"
 *  deadline), on both dashboards. The backend sends more than one declined
 *  status spelling and the exact code isn't always in STATUS_TONE, so detect
 *  it generically from the status code OR its human label — otherwise an
 *  unmapped declined status falls through to the amber/hourglass default. */
function isDeclinedOffer(o: any): boolean {
  const status = (o?.status || '').toString().toUpperCase();
  const label = (o?.statusLabel || '').toString().toUpperCase();
  return status.includes('DECLINE') || status.includes('REJECT') || label.includes('DECLINE');
}

/** Date line under an offer's status pill. Only settled offers get one:
 *  accepted reads "Signed <date>", declined reads "Declined <date>". An offer
 *  still awaiting a response gets no line at all — offers have no expiry date,
 *  so labelling its statusUpdatedDate as "Expires <date>" was misleading. */
function deadlineText(status: string, statusDates: any, statusUpdatedDate: number | null | undefined, declined = false): string {
  if (!declined && status !== 'OFFER-ACCEPTED' && status !== 'OFFER-DECLINED') return '';
  const ts =
    (statusDates && (statusDates[status] || statusDates['OFFERED'])) ??
    statusUpdatedDate ?? null;
  const d = formatShortDate(ts);
  if (!d) return '';
  if (declined) return `Declined ${d}`;
  return status === 'OFFER-ACCEPTED' ? `Signed ${d}` : `Declined ${d}`;
}

/** Absolute URL for a candidate photo. The API stores either a bare filename
 *  (served from `<serverUrl>/download/`) or a full http(s) URL, and can return
 *  the literal string 'null' — treat that the same as no photo. */
function resolvePhotoUrl(photo: any): string {
  if (!photo || typeof photo !== 'string' || photo === 'null') return '';
  return photo.startsWith('http') ? photo : AppSettings.photoUrl + photo;
}

/** Recruiter offer row: candidate-centric (postedBehalfOf + location + engagementType),
 *  all sourced from the candidate record — the offer's context is the candidate, not the job. */
export function mapRecruiterOfferRow(o: any): any {
  return {
    initials: o.candidateInitials || '',
    photoUrl: resolvePhotoUrl(o.candidatePhoto),
    name: [o.candidateFirstName, o.candidateLastName].filter(Boolean).join(' ').trim(),
    role: o.candidateJobTitle || '',
    company: [o.postedBehalfOf, o.location, o.engagementType].filter(Boolean).join(' · '),
    amount: formatRate(o.targetRate, o.payType),
    statusIcon: isDeclinedOffer(o) ? 'tablerX' : (STATUS_ICON[o.status] || 'tablerHourglass'),
    statusText: o.statusLabel || '',
    tone: isDeclinedOffer(o) ? 'red' : (STATUS_TONE[o.status] || 'amber'),
    deadline: deadlineText(o.status, o.statusDates, o.statusUpdatedDate, isDeclinedOffer(o)),
  };
}

/** Bench offer row: candidate-centric (postedBehalfOf + location + engagementType),
 *  same candidate-sourced trio as the recruiter row. */
export function mapBenchOfferRow(o: any): any {
  return {
    initials: o.candidateInitials || '',
    photoUrl: resolvePhotoUrl(o.candidatePhoto),
    name: [o.candidateFirstName, o.candidateLastName].filter(Boolean).join(' ').trim(),
    role: o.candidateJobTitle || '',
    company: [o.postedBehalfOf, o.location, o.engagementType].filter(Boolean).join(' · '),
    amount: formatRate(o.targetRate, o.payType),
    statusIcon: isDeclinedOffer(o) ? 'tablerX' : (STATUS_ICON[o.status] || 'tablerHourglass'),
    statusText: o.statusLabel || '',
    tone: isDeclinedOffer(o) ? 'red' : (STATUS_TONE[o.status] || 'amber'),
    deadline: deadlineText(o.status, o.statusDates, o.statusUpdatedDate, isDeclinedOffer(o)),
  };
}

// ── Recent Submissions ───────────────────────────────────────────────

const STAGE_LABEL: Record<string, string> = {
  'JOB-APPLIED': 'Applied',
  'CANDIDATE-SHORTLISTED': 'Shortlisted',
  'INTERVIEW-SCHEDULED': 'Interview',
  'CANDIDATE-SELECTED': 'Selected',
  'OFFERED': 'Offered',
  'OFFER-ACCEPTED': 'Accepted',
  'OFFER-DECLINED': 'Offer Declined',
  'PRE-ONBOARDED': 'Pre-Onboarded',
  'ONBOARDED': 'Onboarded',
  'SUBMITTED': 'Applied',
  'INTERVIEW-ACCEPTED': 'Interview Accepted',
  'PROFILE-REJECTED': 'Profile Rejected',
};

const STAGE_CLS: Record<string, string> = {
  'JOB-APPLIED': 'stage-applied',
  'CANDIDATE-SHORTLISTED': 'stage-short',
  'INTERVIEW-SCHEDULED': 'stage-interview',
  'CANDIDATE-SELECTED': 'stage-short',
  'OFFERED': 'stage-offered',
  'OFFER-ACCEPTED': 'stage-offered',
  'OFFER-DECLINED': 'stage-rejected',
  'PRE-ONBOARDED': 'stage-offered',
  'ONBOARDED': 'stage-offered',
  'SUBMITTED': 'stage-applied',
  'INTERVIEW-ACCEPTED': 'stage-interview',
  'PROFILE-REJECTED': 'stage-rejected',
};

// Display labels for the backend's `scoreBreakdownData` keys ("title=40",
// "skills=25", ...) — raw keys must never surface in the badge hover text.
const SCORE_BREAKDOWN_LABEL: Record<string, string> = {
  title: 'Title',
  skills: 'Skill',
  location: 'Location',
  experience: 'Experience',
  workMode: 'Work Mode',
};

export function mapRecentSubmissionRow(s: any): any {
  const initials =
    (s.firstName ? s.firstName[0] : '') + (s.lastName ? s.lastName[0] : '');
  const statusKey = s.latestSubmissionStatus || '';

  const rawMatch = s.matchingScore ?? s.matchScore ?? s.score;
  const match: number | null =
    rawMatch == null || rawMatch === '' || isNaN(Number(rawMatch))
      ? null
      : Math.max(0, Math.min(100, Math.round(Number(rawMatch))));

  const breakdown: string[] = Array.isArray(s.scoreBreakdownData) ? s.scoreBreakdownData : [];
  const matchTooltip = breakdown.length
    ? breakdown
      .map(b => {
        const [k, v] = String(b).split('=');
        const key = (k || '').trim();
        const label = SCORE_BREAKDOWN_LABEL[key] || toTitleCase(key);
        return `${label}: ${(v || '').trim()}`;
      })
      .join('\n')
    : '';

  // Wireframe sub-company line (gigsumo_unified(48).html:2195-2219) reads
  // `{engagementType} · {city, state}` — e.g. "Corp To Corp · Remote" or
  // "W2 - Contract · Dallas, California". The earlier port pulled from
  // `latestJobDetails.companyName/location`, which aren't in the actual
  // `dashboard/recentSubmissions` response, so the line came up blank.
  const engagementType = s.candidateDetails?.engagementType || '';
  const candidateLocation = [s.city, s.state].filter(Boolean).join(', ');
  const subCompanyLine = [engagementType, candidateLocation].filter(Boolean).join(' · ');

  return {
    initials: initials.toUpperCase(),
    gradient: 'linear-gradient(135deg,#EDE9FE,#DDD6FE)',
    color: '#6D28D9',
    name: s.candidateName || [s.firstName, s.lastName].filter(Boolean).join(' '),
    job: s.jobTitle || s.candidateDetails?.jobTitle || '',
    company: subCompanyLine,
    stage: STAGE_LABEL[statusKey] || toTitleCase(statusKey) || '',
    stageCls: STAGE_CLS[statusKey] || 'stage-applied',
    // Raw backend status — the badge colour is resolved from FormValidation's
    // APPLICANT_STATUS_COLOR (getStyle) in the component, keyed on this value.
    statusRaw: statusKey,
    date: s.latestSubmissionDate,
    match,
    matchTone: matchTone(match),
    matchTooltip,
  };
}

// ── Pipeline stats (Recruiter + Bench Sales) ────────────────────────

export interface FunnelRow {
  label: string;
  num: number;
  pct: number;
  color?: string;
  text?: string;
  bg?: string;
  border?: string;
}

export function mapRecruiterFunnel(s: any): FunnelRow[] {
  if (!s) return [];
  const applied = Number(s.applicants ?? 0);
  const pct = (n: number) => (applied > 0 ? Math.round((Number(n || 0) / applied) * 100) : 0);
  return [
    { label: 'Applied', num: Number(s.applicants ?? 0), pct: 100, color: 'var(--gs-purple-bg)', text: 'var(--gs-purple)' },
    { label: 'Shortlisted', num: Number(s.shortlisted ?? 0), pct: pct(s.shortlisted), color: 'var(--gs-purple-border)', text: 'var(--gs-purple)' },
    { label: 'Interview', num: Number(s.interview ?? 0), pct: pct(s.interview), color: '#FCD34D', text: 'var(--gs-amber)' },
    { label: 'Selected', num: Number(s.selected ?? 0), pct: pct(s.selected), color: '#BFDBFE', text: 'var(--gs-blue)' },
    { label: 'Offered', num: Number(s.offered ?? 0), pct: pct(s.offered), color: '#A7F3D0', text: 'var(--gs-green)' },
    { label: 'Onboarded', num: Number(s.onboarded ?? 0), pct: pct(s.onboarded), color: '#6EE7B7', text: 'var(--gs-green)' },
  ];
}

export interface FunnelRateCell { val: string; lbl: string; tone: string; mid?: boolean; }

export function mapBenchFunnel(s: any): { rows: FunnelRow[]; rates: FunnelRateCell[] } {
  if (!s) return { rows: [], rates: [] };
  const base = Number(s.submitted ?? 0);
  const pct = (n: number) => (base > 0 ? Math.round((Number(n || 0) / base) * 100) : 0);
  const rows: FunnelRow[] = [
    { label: 'Submitted', num: Number(s.submitted ?? 0), pct: 100, bg: '#DBEAFE', color: '#1E40AF', border: '#2563EB' },
    { label: 'Viewed', num: Number(s.viewed ?? 0), pct: pct(s.viewed), bg: '#EFF6FF', color: '#1E40AF', border: '#93C5FD' },
    { label: 'Interview', num: Number(s.interview ?? 0), pct: pct(s.interview), bg: '#FEF3C7', color: '#92400E', border: '#D97706' },
    { label: 'Selected', num: Number(s.selected ?? 0), pct: pct(s.selected), bg: '#EDE9FE', color: '#6D28D9', border: '#8B5CF6' },
    { label: 'Placed', num: Number(s.placed ?? 0), pct: pct(s.placed), bg: '#D1FAE5', color: '#065F46', border: '#059669' },
    { label: 'Rejected', num: Number(s.rejected ?? 0), pct: pct(s.rejected), bg: '#FEE2E2', color: '#991B1B', border: '#DC2626' },
  ];
  const fmt = (v: any) => `${Number(v ?? 0).toFixed(0)}%`;
  const rates: FunnelRateCell[] = [
    { val: fmt(s.viewedRate?.rate), lbl: 'View Rate', tone: 'blue' },
    { val: fmt(s.interviewRate), lbl: 'Interview Rate', tone: 'amber', mid: true },
    { val: fmt(s.placementRate), lbl: 'Placement Rate', tone: 'green' },
  ];
  return { rows, rates };
}

// ── Today's Activity (both dashboards) ───────────────────────────────

export interface ActivityRow { tone: string; text: string; time: string | number; timeIsLabel?: boolean; }

const ACTIVITY_TONE: Record<string, string> = {
  'JOB-APPLIED': 'green',
  'CANDIDATE-SHORTLISTED': 'purple',
  'INTERVIEW-SCHEDULED': 'blue',
  'INTERVIEW-ACCEPTED': 'blue',
  'CANDIDATE-SELECTED': 'purple',
  'OFFERED': 'amber',
  'OFFER-ACCEPTED': 'green',
  'OFFER-DECLINED': 'red',
  'OFFER-WITHDRAWN': 'red',
  'PROFILE-REJECTED': 'red',
  'INTERVIEW-REJECTED': 'red',
  'INTERVIEW-FAILED': 'red',
  'JOB_CREATED': 'blue',
  'JOB_PUBLISHED': 'blue',
  'JOB_UPDATED': 'gray',
  'JOB_STATUS_UPDATED': 'gray',
  // Like events arrive inline in the events[] feed per
  // TODAYS_ACTIVITY_API_GUIDE.md (May 2026). Doc UI note #2 calls for
  // distinct styling; the template only varies the dot color, so we map
  // to purple (theme highlight) which differentiates from green/amber
  // engagement milestones without colliding with red rejection events.
  'JOB-LIKED':             'purple',
  'JOB_LIKED':             'purple',
  'CANDIDATE-LIKED':       'purple',
  'CANDIDATE_LIKED':       'purple',
  // Candidate-lifecycle and late-pipeline types. These previously had no entry
  // and fell to the `'gray'` default in mapTodayActivity(), so every one of
  // them rendered with the same neutral dot. Additions only — no existing
  // tone above is changed.
  'CANDIDATE_CREATED':       'blue',
  'CANDIDATE_PUBLISHED':     'blue',
  'RESUME_UPLOADED':         'blue',
  'CANDIDATE_UPDATED':       'gray',
  'CANDIDATE_STATUS_UPDATED':'gray',
  'FROZEN':                  'gray',
  'INVALIDATED':             'gray',
  'RESUME_REQUESTED':        'amber',
  'JOB-OFFERED':             'amber',
  'CANDIDATE_INTERESTED':    'purple',
  'CANDIDATE-INTERESTED':    'purple',
  'L1-INTERVIEW':            'purple',
  'L2-INTERVIEW':            'purple',
  'L3-INTERVIEW':            'purple',
  'CANDIDATE_SUBMITTED':     'green',
  'JOB_SUBMITTED':           'green',
  'PRE-ONBOARDED':           'green',
  'ONBOARDED':               'green',
  'CANDIDATE-WITHDRAWN':     'red',
};

/** Convert an unmapped activityType enum like `JOB_STATUS_UPDATED` into a
 *  readable phrase like `Job status updated` so the default branch never
 *  dumps the raw token onto the screen. */
function humanizeActivityType(type: string | null | undefined): string {
  if (!type) return '';
  const words = String(type).replace(/[_-]+/g, ' ').toLowerCase().trim();
  if (!words) return '';
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Tiny HTML-escape for the activity sentences (rendered via [innerHTML]). */
function esc(s: string | null | undefined): string {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Bolded candidate name, or '' when the feed didn't resolve one. Every arm
 *  that uses this must fall back to a generic sentence on '' so we never emit
 *  an empty `<strong></strong>`. */
function candName(e: any): string {
  const n = esc(e?.candidateName).trim();
  return n ? `<strong>${n}</strong>` : '';
}

/** Standard display form for a raw backend status/enum: 'ON_HOLD' -> 'On Hold',
 *  'L1-INTERVIEW' -> 'L1 Interview'. Mirrors FormValidation.formatStatusLabel
 *  (services/FormValidation.ts:269) — copied rather than imported because this
 *  module is DI-free pure functions. */
function statusLabel(v: string | null | undefined): string {
  if (!v) return '';
  return String(v)
    .replace(/[-_]+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase());
}

/** Join two clauses with an em-dash, dropping the separator when either side
 *  is empty so a sentence never ends in a dangling "—". */
function dash(head: string, tail: string): string {
  if (!head) return tail;
  if (!tail) return head;
  return `${head} — ${tail}`;
}

/** Statuses a candidate entity can be set to — used as a whitelist so a real
 *  jobId sitting in the mis-labelled slot can never render as a status. */
const CANDIDATE_STATUSES = new Set([
  'ACTIVE', 'INACTIVE', 'DRAFTED', 'DEACTIVATED', 'BLOCKED', 'EXPIRED',
]);

/** New status for CANDIDATE_STATUS_UPDATED. The backend stores it in the log's
 *  `activityOn` (CandidateService.java:2443), and the feed builder then
 *  re-labels `activityOn` as `jobId` (CandidateService.java:5283) — so it
 *  reaches us as `jobId: "ACTIVE"` with no `jobTitle`. Prefer a real
 *  `status`/`activityOn` field if the backend ever adds one; otherwise read
 *  `jobId`, guarded by the whitelist. Returns '' when nothing usable is
 *  present, so the caller falls back to the status-less sentence. */
function newCandidateStatus(e: any): string {
  const raw = String(e?.status ?? e?.activityOn ?? e?.jobId ?? '').trim();
  return CANDIDATE_STATUSES.has(raw.toUpperCase()) ? statusLabel(raw) : '';
}

function formatActivityTime(createdOn: string | number | Date | null | undefined): string {
  if (createdOn == null || createdOn === '') return '';
  const d = new Date(createdOn);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear()
    && d.getMonth() === now.getMonth()
    && d.getDate() === now.getDate();
  if (sameDay) {
    let hours = d.getHours();
    const mins = d.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${hours}:${mins < 10 ? '0' : ''}${mins} ${ampm}`;
  }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (
    d.getFullYear() === yesterday.getFullYear()
    && d.getMonth() === yesterday.getMonth()
    && d.getDate() === yesterday.getDate()
  ) return 'Yesterday';
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

function sentenceRecruiter(e: any): string {
  const c = `<strong>${esc(e.candidateName)}</strong>`;
  const j = esc(e.jobTitle);
  const jBold = j ? `<strong>${j}</strong>` : '';
  // Used only by the arms added below — `c` above is always bold-wrapped even
  // when the name is missing, which would emit an empty <strong></strong>.
  // `cn` is '' in that case and `who` degrades to the generic noun.
  const cn = candName(e);
  const who = cn || 'Candidate';
  // JOB-LIKED carries `likedByName` / `likedByUserName` (not `candidateName`),
  // so it needs its own arm — falling through to humanize would lose the
  // liker. Job-side feed never gets CANDIDATE-LIKED per the doc.
  // Match both separator forms — the backend emits `JOB_LIKED` (underscore),
  // but this arm historically only checked the hyphen form, so likes fell
  // through to the generic humanize branch ("Job liked — <title>") and lost
  // the liker's name.
  if (e.activityType === 'JOB-LIKED' || e.activityType === 'JOB_LIKED') {
    const liker = esc(e.likedByName) || esc(e.likedByUserName) || 'Someone';
    return jBold ? `<strong>${liker}</strong> liked the ${jBold} job` : `<strong>${liker}</strong> liked your job`;
  }
  switch (e.activityType) {
    case 'JOB-APPLIED': return `${c} applied to ${j}`;
    case 'CANDIDATE-SHORTLISTED': return `${c} was shortlisted for ${j}`;
    case 'INTERVIEW-SCHEDULED':
    case 'INTERVIEW-ACCEPTED': return `Interview confirmed for ${c} — ${j}`;
    case 'CANDIDATE-SELECTED': return `${c} was selected for ${j}`;
    case 'OFFERED': return `Offer sent to ${c} — ${j}`;
    case 'OFFER-ACCEPTED': return `${c} accepted offer — ${j}`;
    case 'OFFER-DECLINED': return `${c} declined offer — ${j}`;
    case 'OFFER-WITHDRAWN': return `Offer withdrawn for ${c} — ${j}`;
    case 'PROFILE-REJECTED': return `${c} rejected for ${j}`;
    case 'INTERVIEW-REJECTED':
    case 'INTERVIEW-FAILED': return `${c} did not pass interview for ${j}`;
    case 'JOB_CREATED': return `New job created — ${jBold}`;
    case 'JOB_PUBLISHED': return `Job published — ${jBold}`;
    case 'JOB_UPDATED': return jBold ? `Job ${jBold} updated` : 'Job updated';
    case 'JOB_STATUS_UPDATED': return jBold ? `Status updated for ${jBold}` : 'Job status updated';
    case 'RESUME_REQUEST_RECEIVED': return `Resume request sent — ${jBold}`;
    // ── Types below had no arm and fell to `default:`, which drops the
    // candidate entirely — that is the "Job submitted — Azure Developer" with
    // no name. Additions only; every arm above is unchanged.
    // JOB_SUBMITTED on the JOB feed is only ever written by the invite flow
    // (CandidateService.java:1826, parentActivity CANDIDATE_INVITATION) —
    // real applications arrive as JOB-APPLIED — so word it as an invitation.
    case 'JOB_SUBMITTED':
      return jBold ? `Invitation sent to ${who} for the ${jBold} position` : `Invitation sent to ${who}`;
    case 'CANDIDATE_SUBMITTED':
      return jBold ? `${who} submitted to ${jBold}` : `${who} submitted`;
    case 'CANDIDATE_INTERESTED':
    case 'CANDIDATE-INTERESTED':
      return jBold ? `${who} invited to ${jBold}` : `${who} invited`;
    case 'L1-INTERVIEW':
    case 'L2-INTERVIEW':
    case 'L3-INTERVIEW':
      return dash(`${who} moved to ${statusLabel(e.activityType)}`, jBold);
    case 'JOB-OFFERED':            return dash(`Offer sent to ${who}`, jBold);
    case 'PRE-ONBOARDED':          return dash(`${who} pre-onboarded`, jBold);
    case 'ONBOARDED':              return dash(`${who} onboarded`, jBold);
    case 'CANDIDATE-WITHDRAWN':
      return jBold ? `${who} withdrew from ${jBold}` : `${who} withdrew`;
    case 'FROZEN':
    case 'INVALIDATED':
      return jBold
        ? `${who} — ${statusLabel(e.activityType)} on ${jBold}`
        : `${who} — ${statusLabel(e.activityType)}`;
    default: {
      const human = humanizeActivityType(e.activityType);
      // Name the candidate when the feed resolved one. With no name this is
      // byte-identical to the previous behaviour.
      if (cn && jBold) return `${cn} — ${human} for ${jBold}`;
      if (cn) return `${cn} — ${human}`;
      return jBold ? `${human} — ${jBold}` : human;
    }
  }
}

function sentenceBench(e: any): string {
  const c = `<strong>${esc(e.candidateName)}</strong>`;
  const j = esc(e.jobTitle);
  const jBold = j ? `<strong>${j}</strong>` : '';
  const co = esc(e.companyName);
  // Used only by the arms added below — `c` above is always bold-wrapped even
  // when the name is missing, which would emit an empty <strong></strong>.
  // `cn` is '' in that case and `who` degrades to the generic noun.
  const cn = candName(e);
  const who = cn || 'Candidate';
  // CANDIDATE-LIKED carries `likedByName` / `likedByUserName` instead of
  // the usual candidate-action shape; surface the liker explicitly so it
  // doesn't fall through to the generic humanize branch.
  // Match both separator forms — the backend emits `CANDIDATE_LIKED`
  // (underscore); the hyphen-only check let likes fall through to the generic
  // humanize branch ("Candidate liked") and lose the liker + candidate name.
  if (e.activityType === 'CANDIDATE-LIKED' || e.activityType === 'CANDIDATE_LIKED') {
    const liker = esc(e.likedByName) || esc(e.likedByUserName) || 'Someone';
    const cnPossessive = cn ? `<strong>${esc(e.candidateName).trim()}'s</strong>` : '';
    return cnPossessive ? `<strong>${liker}</strong> liked ${cnPossessive} profile` : `<strong>${liker}</strong> liked a candidate's profile`;
  }
  switch (e.activityType) {
    case 'JOB-APPLIED': return `${c} submitted to ${j}`;
    case 'CANDIDATE-SHORTLISTED': return `${c} shortlisted for ${j}`;
    case 'INTERVIEW-SCHEDULED':
    case 'INTERVIEW-ACCEPTED': return `Interview confirmed for ${c} — ${j}`;
    case 'CANDIDATE-SELECTED': return `${c} selected by ${co || j}`;
    case 'OFFERED': return `${c} offer received from ${co || j}`;
    case 'OFFER-ACCEPTED': return `${c} accepted offer — ${j}`;
    case 'OFFER-DECLINED': return `${c} declined offer — ${j}`;
    case 'OFFER-WITHDRAWN': return `Offer withdrawn for ${c} — ${j}`;
    case 'PROFILE-REJECTED': return `${c} rejected by ${co || j}`;
    case 'INTERVIEW-REJECTED':
    case 'INTERVIEW-FAILED': return `${c} did not pass interview — ${j}`;
    case 'JOB_CREATED': return `New job created — ${jBold}`;
    case 'JOB_PUBLISHED': return `Job published — ${jBold}`;
    case 'JOB_UPDATED': return jBold ? `Job ${jBold} updated` : 'Job updated';
    case 'JOB_STATUS_UPDATED': return jBold ? `Status updated for ${jBold}` : 'Job status updated';
    case 'CANDIDATE_INTERESTED':
    case 'CANDIDATE-INTERESTED':
      return jBold ? `${who} received an invitation for the ${jBold} position` : `${who} received an invitation`;
    case 'RESUME_REQUEST_INITIATED': return `Resume request received — ${jBold}`;
    // ── Candidate-lifecycle and late-pipeline types below had no arm and fell
    // to `default:`, which drops the candidate entirely — that is the bare
    // "Candidate updated" / "Resume uploaded" on the dashboard. The feed
    // already returns `candidateName` for all of them
    // (CandidateService.buildBenchActivityEvents:5285). Additions only; every
    // arm above is unchanged.
    // CANDIDATE_CREATED and CANDIDATE_PUBLISHED are both written by the same
    // save (CandidateService.java:901 and :970), so they land in the feed at
    // the same timestamp — hence the action-first wording on the create row,
    // so the pair reads as a sequence instead of two near-identical lines.
    case 'CANDIDATE_CREATED':      return dash('New candidate added', cn);
    // Not "to marketplace": the log records publishing to personal / member /
    // business networks with `postedToMarketPlace` as a separate boolean that
    // is often false (CandidateService.java:2579-2602), and the feed builder
    // never returns that `data` map — so the target is unknowable here.
    case 'CANDIDATE_PUBLISHED':    return `${who} profile published`;
    case 'CANDIDATE_UPDATED':      return `${who} profile updated`;
    case 'CANDIDATE_STATUS_UPDATED': {
      const st = newCandidateStatus(e);
      return st ? `${who} status changed to ${st}` : `${who} status updated`;
    }
    case 'RESUME_UPLOADED':
      return cn ? `Resume uploaded for ${cn}` : 'Resume uploaded';
    case 'RESUME_REQUESTED':
      return dash(cn ? `Resume requested for ${cn}` : 'Resume requested', jBold);
    case 'CANDIDATE_SUBMITTED':
    case 'JOB_SUBMITTED':
      return jBold ? `${who} submitted to ${jBold}` : `${who} submitted`;
    case 'L1-INTERVIEW':
    case 'L2-INTERVIEW':
    case 'L3-INTERVIEW':
      return dash(`${who} moved to ${statusLabel(e.activityType)}`, jBold);
    case 'JOB-OFFERED':            return `${who} offer received from ${co || j || 'the client'}`;
    case 'PRE-ONBOARDED':          return dash(`${who} pre-onboarded`, jBold);
    case 'ONBOARDED':              return dash(`${who} onboarded`, jBold);
    case 'CANDIDATE-WITHDRAWN':
      return jBold ? `${who} withdrew from ${jBold}` : `${who} withdrew`;
    case 'FROZEN':
    case 'INVALIDATED':
      return jBold
        ? `${who} — ${statusLabel(e.activityType)} on ${jBold}`
        : `${who} — ${statusLabel(e.activityType)}`;
    default: {
      const human = humanizeActivityType(e.activityType);
      // Name the candidate when the feed resolved one. With no name this is
      // byte-identical to the previous behaviour.
      if (cn && jBold) return `${cn} — ${human} for ${jBold}`;
      if (cn) return `${cn} — ${human}`;
      return jBold ? `${human} — ${jBold}` : human;
    }
  }
}

function viewSummaryRowRecruiter(v: any): ActivityRow {
  const n = Number(v.newViewsCount ?? 0);
  return {
    tone: 'blue',
    text: `Job <strong>${esc(v.jobTitle)}</strong> received ${n} new ${n === 1 ? 'view' : 'views'} overnight`,
    time: 'Yesterday',
    timeIsLabel: true,
  };
}

function viewSummaryRowBench(v: any): ActivityRow {
  const n = Number(v.newViewsCount ?? 0);
  return {
    tone: 'blue',
    text: `<strong>${esc(v.candidateName)}</strong> profile viewed by ${n} ${n === 1 ? 'company' : 'companies'} overnight`,
    time: 'Yesterday',
    timeIsLabel: true,
  };
}

// ── Active Jobs (Recruiter) ─────────────────────────────────────────

const JOB_STATUS_PILL: Record<string, { label: string; tone: string }> = {
  'ACTIVE': { label: 'Active', tone: 'green' },
  'ON_HOLD': { label: 'Paused', tone: 'amber' },
  'FILLED': { label: 'Filled', tone: 'blue' },
  'DRAFTED': { label: 'Draft', tone: 'gray' },
  'CLOSED': { label: 'Closed', tone: 'gray' },
  'EXPIRED': { label: 'Expired', tone: 'red' },
  'BLOCKED': { label: 'Blocked', tone: 'red' },
};

// First Unicode letter of a string ('' if none) — ".Net" → "N", "+" → "".
function titleFirstLetter(s: string): string {
  const m = (s || '').match(/\p{L}/u);
  return m ? m[0] : '';
}

// Letters only, so ".Net + XAMARIN + AZURE Developer" reads "NX", not ".+" —
// same rule as the right-rail job tiles and the job-profile-slider header.
function initialsFromTitle(title: string | undefined | null): string {
  if (!title) return '';
  const words = String(title).trim().split(/\s+/).filter(w => titleFirstLetter(w) !== '');
  if (words.length === 0) return '';
  if (words.length === 1) {
    const letters = words[0].match(/\p{L}/gu) || [];
    return letters.slice(0, 2).join('').toUpperCase();
  }
  return (titleFirstLetter(words[0]) + titleFirstLetter(words[1])).toUpperCase();
}

const JOB_AVATAR_PALETTE = ['#8B5CF6', '#10B981', '#0D9488', '#F59E0B', '#EC4899', '#6366F1', '#2563EB'];
export function deterministicColor(key: string, palette: string[] = JOB_AVATAR_PALETTE): string {
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) & 0xffffffff;
  return palette[Math.abs(hash) % palette.length];
}

export function mapActiveJobRow(j: any): any {
  const loc = [j.city, j.state].filter(Boolean).join(', ');
  const metaParts = [j.clientName, loc, j.jobClassification].filter(Boolean);
  return {
    id: j.jobId,
    initials: initialsFromTitle(j.jobTitle),
    color: deterministicColor(j.jobId || j.jobTitle || ''),
    name: j.jobTitle || '',
    meta: metaParts.join(' · '),
    applied: Number(j.appliedCount ?? 0),
    views: Number(j.viewedByCount ?? 0),
    liked: Number(j.likesCount ?? 0),
    // Raw status kept alongside the pill so the dashboard can gate actions
    // (e.g. Spotlight Invite is disabled while the selected job is BLOCKED).
    status: j.status || '',
    pill: JOB_STATUS_PILL[j.status] ?? { label: j.status || '', tone: 'gray' },
  };
}

// ── Top Candidate Spotlight (Recruiter) ─────────────────────────────

export type SpotlightVariant = 'applicants' | 'talent' | 'market';

export interface SpotlightRow {
  rank: number;
  initials: string;
  color: string;
  name: string;
  job: string;
  meta: string;
  /** `null` when the API doesn't return a score — template renders `-`
   *  for that case (parity with V3 job-card's `noMatchPct` template). */
  match: number | null;
  tone: MatchTone;
  action: string;
  /** Preserved so the Match badge click can open the V3 profile slider
   *  (which needs `candidateId` to refetch via `findByCandidateId`). */
  candidateId: string;
  /** Raw API record — passed verbatim to the slider as `data`, so its
   *  header paints immediately from fields like user.fullname / photo /
   *  jobTitle before the slider's own fetch completes. */
  raw: any;
}

function spotlightMeta(c: any): string {
  const loc = [c.city, c.state].filter(Boolean).join(', ');
  const parts: string[] = [];
  if (loc) parts.push(loc);
  if (c.totalExperience != null && c.totalExperience !== '') parts.push(`${c.totalExperience}+ yrs`);
  if (c.availableIn) parts.push(`Available ${c.availableIn}`);
  if (c.workMode) parts.push(c.workMode);
  return parts.join(' · ');
}

export function mapSpotlightCandidate(
  c: any,
  rank: number,
  variant: SpotlightVariant,
): SpotlightRow {
  const first = c.firstName || c.user?.firstName || '';
  const last = c.lastName || c.user?.lastName || '';
  const fullName = [first, last].filter(Boolean).join(' ').trim() || c.user?.fullname || 'Candidate';
  const initials = ((first[0] || '') + (last[0] || '')).toUpperCase() || fullName.slice(0, 2).toUpperCase();

  // Applicants API uses `matchingScore`; talent + marketplace use `score`.
  // Preserve null when the API omits the score so the template can render
  // `-` instead of a misleading `0%` (matches V3 job-card behavior).
  const rawMatch = variant === 'applicants'
    ? (c.matchingScore ?? c.score)
    : (c.score ?? c.matchScore);
  const match: number | null =
    rawMatch == null || rawMatch === '' || isNaN(Number(rawMatch))
      ? null
      : Math.max(0, Math.min(100, Math.round(Number(rawMatch))));

  return {
    rank,
    initials,
    color: deterministicColor(c.candidateId || fullName),
    name: fullName,
    job: c.jobTitle || '',
    meta: spotlightMeta(c),
    match,
    tone: matchTone(match),
    action: variant === 'applicants' ? 'Review' : variant === 'talent' ? 'Invite' : 'View',
    candidateId: c.candidateId,
    raw: c,
  };
}

// ── Top Job Spotlight (Bench Sales) ─────────────────────────────────

export type SpotlightJobVariant = 'applied' | 'market';

export interface SpotlightJobRow {
  id: string;
  rank: number;
  initials: string;
  color: string;
  gradient: string;
  name: string;          // job title
  meta: string;          // company · location
  sub: string;           // rate · contract type
  /** `null` when the API doesn't return a score — template renders `-`. */
  match: number | null;
  matchTone: MatchTone;
  action: string;
  actionPrimary: boolean;
  /** Raw API record — passed verbatim to the V3 job slider as `data`, so
   *  its header paints immediately from `jobTitle` / `jobReferenceId` /
   *  `jobPostedBy` before the slider's own `findByJobId` completes. */
  raw: any;
}

// Letters only (same rule as initialsFromTitle above) — ".Net + XAMARIN" → "NX".
function jobInitialsFromTitle(title: string | undefined | null): string {
  if (!title) return '??';
  const words = String(title).trim().split(/\s+/).filter(w => titleFirstLetter(w) !== '');
  if (words.length === 0) return '??';
  if (words.length === 1) {
    const letters = words[0].match(/\p{L}/gu) || [];
    return letters.slice(0, 2).join('').toUpperCase() || '??';
  }
  return (titleFirstLetter(words[0]) + titleFirstLetter(words[1])).toUpperCase();
}

function jobMeta(j: any): string {
  const loc = [j.city, j.state].filter(Boolean).join(', ');
  const parts: string[] = [];
  if (j.companyName || j.clientName) parts.push(j.companyName || j.clientName);
  if (loc) parts.push(loc);
  return parts.join(' · ');
}

function jobSub(j: any): string {
  const parts: string[] = [];
  const rate = formatRate(j.targetRate ?? j.payRateFrom, j.payType);
  if (rate) parts.push(rate);
  if (j.contractType || j.engagementType) parts.push(j.contractType || j.engagementType);
  if (j.jobClassification) parts.push(j.jobClassification);
  return parts.join(' · ');
}

export function mapSpotlightJob(
  j: any,
  rank: number,
  variant: SpotlightJobVariant,
): SpotlightJobRow {
  const title = j.jobTitle || '';
  // Preserve null when the API omits the score so the template can render
  // `-` instead of a misleading `0%` (matches V3 job-card behavior).
  const rawMatch = variant === 'applied'
    ? (j.matchingScore ?? j.matchScore ?? j.score)
    : (j.score ?? j.matchScore);
  const match: number | null =
    rawMatch == null || rawMatch === '' || isNaN(Number(rawMatch))
      ? null
      : Math.max(0, Math.min(100, Math.round(Number(rawMatch))));
  return {
    id: j.jobId,
    rank,
    initials: jobInitialsFromTitle(title),
    color: deterministicColor(j.jobId || title),
    gradient: 'linear-gradient(135deg,#EDE9FE,#DDD6FE)',
    name: title,
    meta: jobMeta(j),
    sub: jobSub(j),
    match,
    matchTone: matchTone(match),
    action: variant === 'applied' ? 'View' : 'Apply',
    actionPrimary: variant !== 'applied',
    raw: j,
  };
}

// ── Active Candidates (Bench Sales) ─────────────────────────────────

function candidateStatusPill(status: string, availableIn: string | null | undefined): { label: string; tone: string } {
  if (status === 'BLOCKED') return { label: 'Blocked', tone: 'red' };
  if (status !== 'ACTIVE') return { label: status || '', tone: 'gray' };
  if (availableIn === 'Immediate') return { label: 'Available Now', tone: 'green' };
  if (availableIn) return { label: availableIn, tone: 'amber' };
  return { label: 'Active', tone: 'green' };
}

export function mapActiveCandidateRow(c: any): any {
  const initials = ((c.firstName?.[0] || '') + (c.lastName?.[0] || '')).toUpperCase();
  const loc = [c.city, c.state].filter(Boolean).join(', ');
  const metaParts = [c.jobTitle, c.engagementType, loc].filter(Boolean);
  return {
    id: c.candidateId,
    initials,
    gradient: 'linear-gradient(135deg,#EDE9FE,#DDD6FE)',
    color: '#6D28D9',
    name: [c.firstName, c.lastName].filter(Boolean).join(' ').trim(),
    meta: metaParts.join(' · '),
    status: candidateStatusPill(c.status, c.availableIn),
    match: null,       // API does not return a match score — hide badge
    matchTone: matchTone(null),
  };
}

// ── Job & Candidate Slots widget ───────────────────────────────────

export interface SlotsBlock {
  used: number;
  open: number;
  allocated: number;
  totalPosted: number;
  plan: string;
}

/**
 * Map the dedicated `/dashboard/job-candidate-slots` response into the
 * `SlotsBlock` shape used by the slots widget. The endpoint returns
 * everything the widget needs in one shot.
 *
 * Response shape: `{ data: { slots: { planName, slotsUsed, slotsOpen,
 * allocated, usagePercentage, totalPosted } } }`.
 */
export function mapJobCandidateSlots(response: any): SlotsBlock {
  const s = response?.data?.slots ?? {};
  const used = Number(s.slotsUsed ?? 0);
  const allocated = Number(s.allocated ?? 0);
  const open = Number(s.slotsOpen ?? Math.max(allocated - used, 0));
  const totalPosted = Number(s.totalPosted ?? 0);
  const plan = s.planName
    ? (/plan$/i.test(String(s.planName)) ? String(s.planName) : `${s.planName} Plan`)
    : '';
  return { used, open, allocated, totalPosted, plan };
}

// ── This Week Summary ────────────────────────────────────────────────

export interface ThisWeekRow {
  color: string;
  label: string;
  val: number;
  delta: { text: string; dir: 'up' | 'down' | 'flat' };
}

export interface ThisWeekView { range: string; rows: ThisWeekRow[]; }

interface ThisWeekMetricStat { total?: number; weekCount?: number; change?: number; }

interface MetricSlot { key: string; label: string; color: string; }

const RECRUITER_THIS_WEEK_SLOTS: MetricSlot[] = [
  { key: 'activeJobs', label: 'Active Jobs', color: 'var(--gs-purple-light)' },
  { key: 'applicants', label: 'Applicants', color: 'var(--gs-green)' },
  { key: 'interviews', label: 'Interviews', color: 'var(--gs-amber)' },
  { key: 'shortlisted', label: 'Shortlisted', color: 'var(--gs-blue)' },
  { key: 'offersSent', label: 'Offers Sent', color: 'var(--gs-teal)' },
  { key: 'placements', label: 'Placements', color: '#A7F3D0' },
];

const BENCH_THIS_WEEK_SLOTS: MetricSlot[] = [
  { key: 'onBench', label: 'On Bench', color: 'var(--gs-purple-light)' },
  { key: 'submissions', label: 'Submissions', color: 'var(--gs-green)' },
  { key: 'interviews', label: 'Interviews', color: 'var(--gs-amber)' },
  { key: 'followUpsDue', label: 'Follow-ups Due', color: 'var(--gs-blue)' },
  { key: 'offersOut', label: 'Offers Out', color: 'var(--gs-green)' },
  { key: 'placements', label: 'Placements', color: '#F59E0B' },
];

export function deltaFromChange(change: number | null | undefined): { text: string; dir: 'up' | 'down' | 'flat' } {
  const c = Number(change ?? 0);
  if (c > 0) return { text: `+${c}`, dir: 'up' };
  if (c < 0) return { text: String(c), dir: 'down' };
  return { text: '0', dir: 'flat' };
}

/** Map `/dashboard/this-week-summary` response into the existing template
 *  shape (`{ range, rows[] }`). `followUpsDue` is intentionally null in the
 *  API today — its row stays at zero so the widget layout doesn't shift. */
export function mapThisWeekSummary(
  response: any,
  variant: 'recruiter' | 'bench',
): ThisWeekView | null {
  const summary = response?.data?.summary;
  if (!summary) return null;
  const slots = variant === 'recruiter' ? RECRUITER_THIS_WEEK_SLOTS : BENCH_THIS_WEEK_SLOTS;
  const rows: ThisWeekRow[] = slots.map(slot => {
    const m: ThisWeekMetricStat | null = summary[slot.key];
    return {
      color: slot.color,
      label: slot.label,
      // `weekCount` is the count for the current ISO week window — this is
      // what the "This Week" widget is meant to surface. `total` is the
      // cumulative all-time counter, which the widget previously rendered
      // and made the "+2" change chip visually inconsistent with its
      // number (e.g. Interviews: 14 total vs +2 change this week).
      val: Number(m?.weekCount ?? 0),
      delta: deltaFromChange(m?.change),
    };
  });
  return { range: String(summary.weekRange ?? ''), rows };
}

export function mapTodayActivity(
  events: any[] | null | undefined,
  viewSummary: any[] | null | undefined,
  variant: 'recruiter' | 'bench',
): ActivityRow[] {
  const rows: ActivityRow[] = [];
  for (const e of events || []) {
    const tone = ACTIVITY_TONE[e.activityType] || 'gray';
    const text = variant === 'recruiter' ? sentenceRecruiter(e) : sentenceBench(e);
    // rows.push({ tone, text, time: formatActivityTime(e.createdOn) });
    rows.push({ tone, text, time: e.createdOn });
  }
  for (const v of viewSummary || []) {
    rows.push(variant === 'recruiter' ? viewSummaryRowRecruiter(v) : viewSummaryRowBench(v));
  }
  return rows;
}
